/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project = feature.project;
    let lcdFeatureType = 'lcd';

    let LCDFeature = feature.project.getFeature('lcd');
    if (LCDFeature === undefined) {
        LCDFeature = feature.project.getFeature('ds_oled');
        lcdFeatureType = 'ds_oled';
    }
    if (LCDFeature === undefined) {
        return blocks;
    }
    const BACKCOLOR = PlatformUtils.hexToTibboColor(LCDFeature.getSettingValue('BACKCOLOR'));
    const FORECOLOR = PlatformUtils.hexToTibboColor(LCDFeature.getSettingValue('FORECOLOR'));
    const LCDMenuFeatureEnabled = LCDFeature && LCDFeature.getSettingsEnabled();
    const SCREEN_WIDTH = LCDFeature.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS');
    const SCREEN_HEIGHT = LCDFeature.getSettingValue('LCD_SCREEN_WIDGET_ROWS');
    let LCD_TEXT_WIDGET_MAX_LENGTH = '64';
    if (lcdFeatureType === 'ds_oled') {
        LCD_TEXT_WIDGET_MAX_LENGTH = '16';
    }

    let LCD_TEXT_WIDGETS_COUNT = 0;
    let LCD_IMG_WIDGETS_COUNT = 0;
    for (let i = 0; i < project.features.length; i++) {
        const feature = project.features[i];
        for (let j = 0; j < feature.settings.length; j++) {
            const setting = feature.settings[j];
            if (setting.refId === 'display_screen'
                && setting.settingType === SettingType.ARRAY) {
                const SCREENS = feature.getSettingValue('SCREENS');
                for (let k = 0; k < SCREENS.length; k++) {
                    const widgets = SCREENS[k].getSettingValue('WIDGETS');
                    for (let l = 0; l < widgets.length; l++) {
                        if (widgets[l].id === 'lcd_text_widget') {
                            LCD_TEXT_WIDGETS_COUNT++;
                        } else if (widgets[l].id === 'lcd_image_widget') {
                            LCD_IMG_WIDGETS_COUNT++;
                        }
                    }
                }
            }
        }
    }

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `
type lcd_img_widget
    filename as string(50)
    x_pos as short
    y_pos as byte
    x_offset as word
    y_offset as word
    width as word
    height as word
    visible as boolean
    screen as byte
end type

type lcd_text_widget
    text as string(${LCD_TEXT_WIDGET_MAX_LENGTH})
    x_pos as short
    y_pos as byte
    width as word
    height as word
    forecolor as word
    backcolor as word
    font as string(16)
    text_alignment as pl_lcd_text_alignment
    visible as boolean
    screen as byte
end type


#define LCD_IMG_WIDGETS_COUNT ${LCD_IMG_WIDGETS_COUNT}
#define LCD_TEXT_WIDGETS_COUNT ${LCD_TEXT_WIDGETS_COUNT}

`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
declare sub lcd_display_widgets(screen as byte)
declare sub lcd_display_img_widget(lcd_image_widget as lcd_img_widget)
declare sub lcd_display_text_widget(lcd_text_widget as lcd_text_widget)
declare sub lcd_init_widgets()

declare lcd_img_widgets as lcd_img_widget(LCD_IMG_WIDGETS_COUNT)
declare lcd_text_widgets as lcd_text_widget(LCD_TEXT_WIDGETS_COUNT)
declare display_current_screen as byte
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
'LCD setup
'================================================================
lcd.width=${SCREEN_WIDTH}
lcd.height=${SCREEN_HEIGHT}
`,
    });

    if (lcdFeatureType === 'lcd') {
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
io.num=PL_IO_NUM_55
io.enabled=YES
io.num=PL_IO_NUM_43
io.enabled=YES
io.num=PL_IO_NUM_42
io.enabled=YES
io.num=PL_IO_NUM_41
io.enabled=YES
io.num=PL_IO_NUM_40
io.enabled=YES
lcd.iomapping="55,43,42,41,40,0" 'RST,DC,WR,RD,CS,data_bus
lcd.rotated=YES 'yes, the LCD is installed up side down
'backlight ON
io.num=PL_IO_NUM_54 
io.enabled=YES
io.state=LOW
`,
        });
    } else if (lcdFeatureType === 'ds_oled') {
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `

io.num=36
io.enabled=YES

io.num=35
io.enabled=YES

io.num=34
io.enabled=YES

io.num=33
io.enabled=YES
io.state=HIGH 'disabled

io.num=32
io.enabled=YES
io.state=LOW 'permanently enabled

lcd.width=96
lcd.height=32
lcd.iomapping="36,35,34,33,32,0" 'RST,DC,WR,RD,CS,data_bus
`,
        });
    }

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
lcd.enabled=YES
lcd.forecolor=${BACKCOLOR}
lcd.lock
lcd.fill(0,0,lcd.width,lcd.height)
lcd.unlock
'sets font used to draw text
romfile.open(LOG_FONT_FILE)
lcd.setfont(romfile.offset)
'-----------------------------------------------------------------
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
lcd_init_widgets()
lcd_display_widgets(0)`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        template: `
dim lcd_img_widgets as lcd_img_widget(LCD_IMG_WIDGETS_COUNT)
dim lcd_text_widgets as lcd_text_widget(LCD_TEXT_WIDGETS_COUNT)
dim display_current_screen as byte = 0
`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub lcd_init_widgets()
'****lcd_init_widgets****'
end sub
    
sub lcd_display_widgets(screen as byte)
dim i as byte
dim lcd_image_widget as lcd_img_widget
dim lcd_text_widget as lcd_text_widget

display_current_screen=screen

lcd.forecolor=${BACKCOLOR}
lcd.lock
lcd.fill(0,0,lcd.width,lcd.height)
lcd.unlock

if LCD_TEXT_WIDGETS_COUNT > 0 then
    for i = 0 to LCD_TEXT_WIDGETS_COUNT - 1
        lcd_text_widget = lcd_text_widgets(i)
        lcd_display_text_widget(lcd_text_widget)
    next i
end if

if LCD_IMG_WIDGETS_COUNT > 0 then
    for i = 0 to LCD_IMG_WIDGETS_COUNT - 1
        lcd_image_widget = lcd_img_widgets(i)
        lcd_display_img_widget(lcd_image_widget)
    next
end if
#ifdef LCD_GRAPH_WIDGETS_COUNT
if LCD_GRAPH_WIDGETS_COUNT > 0 then
    for i = 0 to LCD_GRAPH_WIDGETS_COUNT - 1
        if lcd_graph_widgets(i).screen = display_current_screen then
            lcd_graph_init(lcd_graph_widgets(i))
        end if
    next i
end if
#endif
end sub`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub lcd_display_img_widget(lcd_image_widget as lcd_img_widget)
    ${LCDMenuFeatureEnabled ? `
    if ui_active = true then exit sub
    ` : ''}
    if lcd_image_widget.screen <> display_current_screen then
        exit sub
    end if
    lcd.lock
    lcd.forecolor=${BACKCOLOR}
    lcd.fill(lcd_image_widget.x_pos, lcd_image_widget.y_pos, lcd_image_widget.width, lcd_image_widget.height)
    if lcd_image_widget.visible<>false AND lcd_image_widget.filename <> "" then
        romfile.open(lcd_image_widget.filename)
        lcd.bmp(romfile.offset, lcd_image_widget.x_pos, lcd_image_widget.y_pos, lcd_image_widget.x_offset, lcd_image_widget.y_offset, lcd_image_widget.width, lcd_image_widget.height)
    end if
    lcd.unlock
end sub

sub lcd_display_text_widget(lcd_text_widget as lcd_text_widget)
    ${LCDMenuFeatureEnabled ? `
    if ui_active = true then exit sub
    ` : ''}
    dim text_to_print as string=lcd_text_widget.text
    if lcd_text_widget.screen <> display_current_screen then
        exit sub
    end if
    lcd.forecolor=lcd_text_widget.backcolor
    lcd.lock
    lcd.fill(lcd_text_widget.x_pos, lcd_text_widget.y_pos, lcd_text_widget.width, lcd_text_widget.height)
    if lcd_text_widget.visible<>false then
        lcd.forecolor=lcd_text_widget.forecolor
        romfile.open(lcd_text_widget.font)
        lcd.setfont(romfile.offset)
        lcd.textorientation=PL_LCD_TEXT_ORIENTATION_0
        lcd.textverticalspacing=1
        lcd.texthorizontalspacing=1
        lcd.forecolor=lcd_text_widget.forecolor
        lcd.backcolor=lcd_text_widget.backcolor
        lcd.textalignment=lcd_text_widget.text_alignment
        while lcd.getprintwidth(text_to_print) > lcd_text_widget.width
            text_to_print = mid(text_to_print, 1, len(text_to_print) - 1)
        wend
        lcd.printaligned(text_to_print, lcd_text_widget.x_pos, lcd_text_widget.y_pos, lcd_text_widget.width, lcd_text_widget.height)
    end if
    lcd.unlock
end sub`,
    });


    return blocks;
};
